import React from 'react'
import Tippy from '@tippyjs/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { BadgeArea, BaseButton, CardHeader, DefaultCard } from 'iebt-zing-storybook'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { IMeetingRoom } from '../types'
import { getHubRoute } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import { trans } from 'sharedKernel/i18n'
import { followCursor } from 'tippy.js'
import {
  MeetingRoomGrid,
  StatusBadge,
} from './styles'

interface MeetingRoomListProps {
  rooms: IMeetingRoom[]
  onDelete: (id: string) => void
}

const MeetingRoomCard = ({
  room,
  onDelete,
}: {
  room: IMeetingRoom
  onDelete: (id: string) => void
}): JSX.Element => {
  const navigate = useNavigate()
  const coverImage = room.coverImages?.[0]?.path ?? room.images?.[0]?.path
  const isAvailable = room.status === 'available'
  const detailRoute = getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_DETAIL).replace(
    ':meetingRoomId',
    room.id
  )
  const badges = (
    <BadgeArea>
      <StatusBadge $isAvailable={isAvailable}>
        <FontAwesomeIcon icon={['fas', 'signal']} />
        {trans('meetingRoom.list.statusLabel')}:{' '}
        {isAvailable
          ? trans('meetingRoom.list.statusAvailable')
          : trans('meetingRoom.list.statusUnavailable')}
      </StatusBadge>
    </BadgeArea>
  )

  const handleEdit = (): void => {
    const editRoute = getHubRoute(ROUTES.HUB.COMPANY.MEETING_ROOM_EDIT).replace(
      ':meetingRoomId',
      room.id
    )
    navigate(editRoute)
  }

  const buttons = [
    <Tippy
      content={trans('meetingRoom.list.edit')}
      followCursor={true}
      plugins={[followCursor]}
      animation="scale"
      arrow={false}
      theme="primary"
    >
      <div>
        <BaseButton
          variant="risk"
          size="medium"
          onClick={handleEdit}
          btnContent={trans('meetingRoom.list.edit')}
        />
      </div>
    </Tippy>,
    <Tippy
      content={trans('meetingRoom.list.delete')}
      followCursor={true}
      plugins={[followCursor]}
      animation="scale"
      arrow={false}
      theme="primary"
    >
      <div>
        <BaseButton
          variant="risk-secondary"
          size="medium"
          onClick={() => onDelete(room.id)}
          btnContent={trans('meetingRoom.list.delete')}
        />
      </div>
    </Tippy>,
  ]

  return (
    <DefaultCard
      title={room.name}
      shortDescription={room.description}
      badges={badges}
      header={<CardHeader coverImage={coverImage} />}
      buttons={buttons}
      sendTo={() => navigate(detailRoute)}
    />
  )
}

export const MeetingRoomList = ({
  rooms,
  onDelete,
}: MeetingRoomListProps): JSX.Element => {
  return (
    <MeetingRoomGrid>
      {rooms.map(room => (
        <MeetingRoomCard
          key={room.id}
          room={room}
          onDelete={onDelete}
        />
      ))}
    </MeetingRoomGrid>
  )
}
